import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import ChatInput from "./ChatInput";

const DEFAULT_PLACEHOLDER =
  "Ask me about coaching conversations, workplace challenges, performance situations...";

const WelcomeScreen = ({
  userInput,
  setUserInput,
  handleSend,
  sampleQuestions,
}) => {
  const [placeholder, setPlaceholder] = useState(DEFAULT_PLACEHOLDER);
  // Set by a "focus" chip that carries a promptPrefix, and consumed by the
  // next send so the model gets that chip's instructions wrapped around
  // whatever the user pastes.
  const [promptPrefix, setPromptPrefix] = useState(null);
  const inputRef = useRef(null);

  const handleSampleQuestion = (question) => {
    if (typeof question === "string") {
      handleSend(question);
      return;
    }

    if (question.action === "focus") {
      setPlaceholder(question.placeholder || DEFAULT_PLACEHOLDER);
      setPromptPrefix(question.promptPrefix || null);
      inputRef.current?.focus();
      return;
    }

    handleSend(question.label);
  };

  const sendFromInput = () => {
    // Guard so an empty send doesn't silently disarm the prefix.
    if (!userInput.trim()) return;
    handleSend(null, promptPrefix ? { promptPrefix } : {});
    setPromptPrefix(null);
  };

  return (
    <div className="col-start-1 row-start-1 flex w-full flex-col items-center">
      {/*
        Title + subtitle: fades upward while the card below grows. The exit
        matches the 0.45s morph so this subtree (which still holds the
        outgoing shell) stays mounted until the crossfade has finished.
      */}
      <motion.div
        className="flex flex-col items-center w-full"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.3 } }}
        exit={{ opacity: 0, y: -16, transition: { duration: 0.45, ease: "easeInOut" } }}
      >
        <h1
          className="text-gray-900 dark:text-white mt-8 mb-2 text-center"
          style={{
            fontSize: '3.75rem',
            lineHeight: '1.1',
            fontWeight: 'bold'
          }}
        >
          CoachAI
        </h1>

        <h2
          className="text-gray-700 dark:text-gray-300 mb-8 text-center"
          style={{
            fontSize: '1rem',
            lineHeight: '1.5'
          }}
        >
          Your personal Conversational Coaching Assistant
        </h2>
      </motion.div>

      {/*
        Shares layoutId "chatShell" with AnimatedChatUI's outer container, so
        this bar morphs into the chat card instead of being swapped out.
        Keep the surface classes in sync with that container.
      */}
      <motion.div
        layoutId="chatShell"
        transition={{ layout: { duration: 0.45, ease: "easeInOut" } }}
        className="w-full max-w-3xl p-4 mb-8 flex items-center bg-white/70 dark:bg-gray-800/80 shadow-lg rounded-2xl border border-gray-100/30 dark:border-gray-700/50 backdrop-blur-sm"
      >
        <ChatInput
          ref={inputRef}
          value={userInput}
          onChange={setUserInput}
          onSend={sendFromInput}
          placeholder={placeholder}
        />
      </motion.div>

      {/* Sample questions: fades upward while the card grows. */}
      <motion.div
        className="flex flex-col items-center w-full"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.3 } }}
        exit={{ opacity: 0, y: -16, transition: { duration: 0.45, ease: "easeInOut" } }}
      >
        <div className="mt-4 text-gray-700 dark:text-gray-300 text-base font-medium">
          Sample Questions You Can Ask
        </div>

        <div className="mt-4 flex flex-col items-center space-y-3 w-full max-w-3xl">
          {sampleQuestions.map((question, index) => (
            <button
              key={index}
              type="button"
              className="w-full bg-white/60 text-gray-900 py-3 px-6 rounded-full text-left transition hover:bg-white/75 dark:bg-gray-900/60 dark:text-white dark:hover:bg-gray-800/70 shadow-md border border-gray-100/30 dark:border-gray-800/40 backdrop-blur-sm"
              onClick={() => handleSampleQuestion(question)}
            >
              {typeof question === "string" ? question : question.label}
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default WelcomeScreen;
